import React, { useEffect } from "react";
import Countdown from "../components/Countdown";
import Timeline from "../components/Timeline";
import EventMap from "../components/EventMap";
import {
  ArrowUpRight, Calendar, CalendarPlus, MapPin, Sparkles, Car, TrainFront, ParkingCircle,
  Shirt, HeartHandshake, Phone, ClipboardCheck, Heart, CarTaxiFront,
} from "lucide-react";

const maps = (q) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
const MAPS_EVENT = maps("Mauttorpromenade 7, 5400 Hallein");
const MAPS_PARKING = maps("Mauttorpromenade 12, 5400 Hallein");
const NAVI_EVENT = "https://www.google.com/maps/dir/?api=1&destination=" + encodeURIComponent("Mauttorpromenade 12, 5400 Hallein");
const REGISTER_URL = "https://tally.so/r/LZ4kdp?hotel_status=selfpay&plusone_allowed=yes";

const gcal = (title, start) =>
  "https://calendar.google.com/calendar/render?action=TEMPLATE" +
  `&text=${encodeURIComponent(title)}&dates=${start}/20260926T000000Z` +
  `&location=${encodeURIComponent("Schmiede Hallein / Alte Saline, Mauttorpromenade 7, 5400 Hallein")}` +
  `&details=${encodeURIComponent("Alle Infos: https://crtrstudios.com/eventinfo")}`;

const CalBtn = ({ ics, gcalHref, id }) => (
  <div className="mt-4 flex flex-col items-start gap-2">
    <a href={ics} download data-testid={`eventinfo-cal-${id}`}
      className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-white/10 px-4 py-2 text-xs font-medium text-white transition-colors hover:bg-[var(--crtr-yellow)] hover:text-black">
      <CalendarPlus className="h-4 w-4" /> In Kalender speichern
    </a>
    <a href={gcalHref} target="_blank" rel="noreferrer" data-testid={`eventinfo-gcal-${id}`}
      className="pl-1 text-[11px] text-white/45 underline-offset-2 transition-colors hover:text-[var(--crtr-yellow)] hover:underline">
      Google Kalender
    </a>
  </div>
);

const Section = ({ id, icon: Icon, kicker, title, children }) => (
  <section id={id} data-testid={`eventinfo-${id}`} className="border-t border-white/10 py-12 md:py-16">
    <div className="grid gap-6 md:grid-cols-[220px_1fr] md:gap-12">
      <div>
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5 text-[var(--crtr-yellow)]" strokeWidth={1.7} />
          <span className="mono-label text-[11px] text-white/50">{kicker}</span>
        </div>
        <h2 className="font-display mt-3 text-3xl text-white md:text-4xl">{title}</h2>
      </div>
      <div className="space-y-5 text-[15px] leading-relaxed text-white/75 md:text-base">{children}</div>
    </div>
  </section>
);

const LinkBtn = ({ href, children, testId }) => (
  <a href={href} target="_blank" rel="noreferrer" data-testid={testId}
    className="group inline-flex items-center gap-2 rounded-full border border-[var(--crtr-yellow)]/60 px-5 py-2.5 text-sm font-medium text-[var(--crtr-yellow)] transition-colors hover:bg-[var(--crtr-yellow)] hover:text-black">
    {children}
    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </a>
);

const Tel = ({ number, testId }) => (
  <a href={`tel:${number.replace(/\s/g, "")}`} data-testid={testId}
    className="inline-flex items-center gap-2 font-medium text-white transition-colors hover:text-[var(--crtr-yellow)]">
    <Phone className="h-4 w-4 text-[var(--crtr-yellow)]" /> {number}
  </a>
);

const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-white/10 bg-black/25 p-6 backdrop-blur-sm ${className}`}>{children}</div>
);

export default function EventInfo() {
  useEffect(() => {
    document.title = "Event Info | CRTR Studios";
    const existing = document.querySelector('meta[name="robots"]');
    const m = existing || document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    if (!existing) document.head.appendChild(m);
    return () => { if (!existing) document.head.removeChild(m); document.title = "CRTR Studios | Creator Marketing & Creator Economy"; };
  }, []);

  return (
    <div className="relative min-h-screen px-5 pb-28 pt-32 md:px-10 md:pt-40" data-testid="eventinfo-page">
      <div className="mx-auto max-w-5xl">
        <span className="mono-label text-xs text-[var(--crtr-yellow)]">CRTR Experience · Hallein</span>
        <h1 className="font-display mt-4 text-4xl text-white sm:text-5xl lg:text-6xl">
          Hey, bald ist es<br />so weit<span className="text-[var(--crtr-yellow)]">.</span>
        </h1>
        <div className="mt-8 max-w-2xl space-y-4 text-base leading-relaxed text-white/75 md:text-lg">
          <p>Am <strong className="text-white">Freitag, den 25. September</strong>, öffnen sich die Türen zu unserer gemeinsamen CRTR Experience auf der Pernerinsel in Hallein.</p>
          <p>Und falls du zu Hause schon ungeduldig mit den Füßen scharrst, dir erste Gedanken über Outfit, Anreise und Heimfahrt machst oder einfach wissen möchtest, was dich bei uns erwartet: <strong className="text-white">We got you!</strong></p>
          <p>Hier findest du alles, was du für einen entspannten Eventtag brauchst – kompakt, übersichtlich und jederzeit griffbereit auf deinem Smartphone.</p>
        </div>
        <Countdown />

        <div className="mt-14 grid gap-4 md:grid-cols-2">
          <Card className="glow-teal relative overflow-hidden">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-[var(--crtr-yellow)]" strokeWidth={1.7} />
              <span className="mono-label text-[11px] text-white/50">When</span>
            </div>
            <div className="mt-6 grid gap-6 sm:grid-cols-2">
              <div data-testid="eventinfo-when-creators">
                <span className="mono-label block text-[10px] text-[var(--crtr-yellow)]">Für Creators</span>
                <p className="mt-2 text-white">Freitag, 25. September 2026</p>
                <p className="font-display mt-1 text-3xl text-white">14:00</p>
                <p className="text-sm text-white/55">Start</p>
                <CalBtn id="creators" ics="/crtr-experience-creators.ics" gcalHref={gcal("CRTR Experience Hallein (Creators)", "20260925T120000Z")} />
              </div>
              <div data-testid="eventinfo-when-guests">
                <span className="mono-label block text-[10px] text-[var(--crtr-yellow)]">Für Gäste</span>
                <p className="mt-2 text-white">Freitag, 25. September 2026</p>
                <p className="font-display mt-1 text-3xl text-white">15:30</p>
                <p className="text-sm text-white/55">Start · Open End</p>
                <CalBtn id="guests" ics="/crtr-experience-gaeste.ics" gcalHref={gcal("CRTR Experience Hallein", "20260925T133000Z")} />
              </div>
            </div>
          </Card>
          <Card className="glow-violet relative overflow-hidden">
            <div className="flex items-center gap-3">
              <MapPin className="h-5 w-5 text-[var(--crtr-yellow)]" strokeWidth={1.7} />
              <span className="mono-label text-[11px] text-white/50">Where</span>
            </div>
            <p className="font-display mt-6 text-2xl text-white">Schmiede Hallein / Alte Saline</p>
            <p className="mt-2 leading-relaxed text-white/75">Pernerinsel<br />Mauttorpromenade 7<br />5400 Hallein</p>
            <div className="mt-6">
              <LinkBtn href={MAPS_EVENT} testId="eventinfo-maps-route">Route und Eingang in Google Maps öffnen</LinkBtn>
            </div>
          </Card>
        </div>

        <div className="mt-20">
          <Section id="happening" icon={Sparkles} kicker="Programm" title="What is happening">
            <p>Freu dich auf einen Nachmittag und Abend rund um <strong className="text-white">Creators, Commerce &amp; Culture</strong> – mit Schmiede Experience, Creator Sessions, der SUPERSPÄTI × MC FITTI Product Premiere, Live Shopping, Festival Dinner im Streetfood-Style à la Superspäti Berlin, Werkschau, der Superspäti × MC Fitti Show und anschließender CRTR Studios × Superspäti Aftershow Party mit DJ Phil Preuss &amp; Friends sowie TULATU live.</p>
            <p>Dazwischen bleibt natürlich genug Zeit zum Kennenlernen, Austauschen, Essen, Trinken und gemeinsamen Feiern.</p>
            <div className="flex flex-wrap gap-2 pt-2">
              {["Schmiede Experience", "Creator Sessions", "SUPERSPÄTI × MC FITTI Premiere", "Live Shopping", "Festival Dinner · Streetfood-Style", "Werkschau", "Superspäti × MC Fitti Show", "Aftershow Party · DJ Phil Preuss & Friends", "Live: DJ Phil Preuss & TULATU"].map((t) => (
                <span key={t} className="mono-label rounded-full border border-white/15 px-3 py-1.5 text-[10px] text-white/70">{t}</span>
              ))}
            </div>
            <Timeline />
          </Section>

          <Section id="anreise" icon={Car} kicker="Anreise" title="How to get there">
            <EventMap />
            <div className="grid gap-4 sm:grid-cols-2">
              <Card>
                <Car className="h-5 w-5 text-[var(--crtr-yellow)]" strokeWidth={1.7} />
                <h3 className="font-heavy mt-4 text-lg text-white">Mit dem Auto</h3>
                <p className="mt-2 text-sm leading-relaxed">Über die Autobahn in Richtung Hallein und anschließend der Beschilderung Richtung Stadtzentrum und Pernerinsel folgen. Die Eventlocation befindet sich in der Alten Saline auf der Pernerinsel.</p>
                <div className="mt-5"><LinkBtn href={NAVI_EVENT} testId="eventinfo-navi">Navigation starten</LinkBtn></div>
              </Card>
              <Card>
                <TrainFront className="h-5 w-5 text-[var(--crtr-yellow)]" strokeWidth={1.7} />
                <h3 className="font-heavy mt-4 text-lg text-white">Mit Zug oder S-Bahn</h3>
                <p className="mt-2 text-sm leading-relaxed">Mit Zug oder S-Bahn bis zum Bahnhof Hallein fahren. Von dort erreichst du die Pernerinsel und die Eventlocation in rund fünf Minuten zu Fuß.</p>
              </Card>
            </div>
          </Section>

          <Section id="parken" icon={ParkingCircle} kicker="Parken & Eingang" title="Parking & Entrance">
            <p>Direkt hinter dem Eventgebäude befindet sich beim Kreisverkehr ein großer gebührenpflichtiger Parkplatz. <strong className="text-white">Die ersten zwei Stunden parkst du kostenlos.</strong> Von dort sind es nur wenige Minuten zu Fuß bis zum Eingang.</p>
            <LinkBtn href={MAPS_PARKING} testId="eventinfo-maps-parking">Parkplatz Pernerinsel in Google Maps öffnen</LinkBtn>
            <p>Bitte nutze den ausgewiesenen Eingang zur Schmiede und folge vor Ort der CRTR-Beschilderung.</p>
            <LinkBtn href={MAPS_EVENT} testId="eventinfo-maps-entrance">Eingang zur Eventlocation in Google Maps öffnen</LinkBtn>
          </Section>

          <Section id="taxi" icon={CarTaxiFront} kicker="Taxi" title="Taxi">
            <p>Für individuelle An- und Abreise erreicht ihr Salzburg Taxi unter:</p>
            <Tel number="+43 662 8111" testId="eventinfo-tel-taxi" />
          </Section>

          <Section id="dresscode" icon={Shirt} kicker="Dresscode" title="Come as you are.">
            <p>Die Schmiede ist eine echte Industrial Location und kann – vor allem später am Abend – ziemlich kühl werden. <strong className="text-white">Pack dir deshalb unbedingt eine warme Jacke oder etwas zum Überziehen ein.</strong></p>
          </Section>

          <Section id="feelgood" icon={HeartHandshake} kicker="Vor Ort" title="Your Feelgood Manager">
            <p>Für alle Fragen, Wünsche und kleinen Herausforderungen vor Ort ist <strong className="text-white">Cristina Pacurar</strong> eure persönliche Feelgood Managerin. Sprecht sie jederzeit gerne an – Cristina ist für euch da.</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Card className="flex items-center gap-5">
                <img src="/images/cristina.jpg" alt="Cristina Pacurar" loading="lazy" data-testid="eventinfo-cristina-photo"
                  className="h-24 w-24 flex-shrink-0 rounded-full border-2 border-[var(--crtr-yellow)] object-cover sm:h-28 sm:w-28" />
                <div>
                  <span className="mono-label block text-[10px] text-white/50">Feelgood Managerin</span>
                  <p className="font-heavy mt-2 text-lg text-white">Cristina Pacurar</p>
                </div>
              </Card>
              <Card>
                <span className="mono-label block text-[10px] text-white/50">Fragen am Eventtag</span>
                <p className="font-heavy mt-2 text-lg text-white">Emilie Mayer</p>
                <div className="mt-3"><Tel number="+43 664 73163144" testId="eventinfo-tel-emilie" /></div>
              </Card>
            </div>
          </Section>

          <Section id="anmeldung" icon={ClipboardCheck} kicker="Anmeldung" title="One more thing">
            <p>Wir haben eine ziemlich gut gepflegte Gästeliste und dank vieler persönlicher Gespräche auch einen guten Überblick. Trotzdem unsere herzliche Bitte an alle, die uns die Organisation ein kleines Stück leichter machen möchten:</p>
            <p>Alle, die sich bis jetzt noch nicht offiziell über diesen Link angemeldet haben, bitten wir dies noch über den folgenden Link schnell und unkompliziert zu tun, damit wir für alle eine optimale Experience organisieren können.</p>
            <a href={REGISTER_URL} target="_blank" rel="noreferrer" data-testid="eventinfo-register"
              className="group inline-flex items-center gap-3 rounded-full bg-[var(--crtr-yellow)] py-2.5 pl-6 pr-2.5">
              <span className="mono-label text-xs text-black">Jetzt anmelden</span>
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black transition-transform group-hover:translate-x-0.5">
                <ArrowUpRight className="h-4 w-4 text-[var(--crtr-yellow)]" />
              </span>
            </a>
            <p>So können wir sichergehen, dass wirklich alle Namen, Begleitpersonen und wichtigen Informationen richtig bei uns angekommen sind. Vielen herzlichen Dank!</p>
          </Section>

          <section data-testid="eventinfo-outro" className="border-t border-white/10 pt-16">
            <Heart className="h-6 w-6 text-[var(--crtr-yellow)]" strokeWidth={1.7} />
            <h2 className="font-display mt-4 text-4xl text-white sm:text-5xl">See you in Hallein<span className="text-[var(--crtr-yellow)]">.</span></h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/75 md:text-lg">Wir freuen uns riesig auf euch, auf inspirierende Begegnungen, neue Ideen, gute Gespräche und eine gemeinsame Experience, die hoffentlich noch lange in Erinnerung bleibt.</p>
            <p className="mt-8 text-white/75">Liebe Grüße aus Salzburg</p>
            <p className="font-heavy mt-1 text-lg text-white">Dominik, Sebastian, Emilie und Fritz</p>
          </section>
        </div>
      </div>
    </div>
  );
}
